import { ReactNode, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@fronterahealth/frontera-ui-components";
import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@fronterahealth/frontera-ui-components/ui/tooltip";

import { EhrSyncAlertsType } from "@api/graphql/types-and-hooks";
import { EhrSyncAlertPopup } from "@components/EHRSyncAlert/EhrSyncAlertPopup";
import { WarningTriangle } from "@components/EHRSyncAlert/WarningTriangle";
import { LayoutFooter } from "@components/Layout";
import { FormSavingIndicator } from "@components/ReportField/FormSavingIndicator";
import { useHasUnsavedFields } from "@components/ReportField/useHasUnsavedFields";
import { notifyError } from "@components/notifications/notifications";
import { useOrderedReportSubRoutes } from "@pages/ClinicalReportDetails/Components/hooks";
import { useAdminData } from "@providers/AdminDataProvider";
import { ClinicalReportPageRoute } from "@routes/ClinicalReports/ClinicalReportsHelper";

interface ClinicalReportFooterButtonRowProps {
  primaryButtonTitle?: string;
  primaryButtonLoading?: boolean;
  primaryButtonDisabled?: boolean;
  primaryButtonType?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
  primaryButtonAction?: () => Promise<unknown>;
  primaryButtonDisabledTooltipReason?: ReactNode;
  skipNavigate?: boolean;
  hideBackButton?: boolean;
  dataTestID?: string;
  ehrSyncAlerts?: EhrSyncAlertsType | null | undefined;
}

export const ClinicalReportFooterButtonRow: React.FC<ClinicalReportFooterButtonRowProps> = ({
  primaryButtonAction,
  primaryButtonType = "button",
  primaryButtonTitle = "Next Step",
  primaryButtonDisabled = false,
  primaryButtonDisabledTooltipReason = "",
  primaryButtonLoading,
  skipNavigate = false,
  hideBackButton = false,
  dataTestID = "",
  ehrSyncAlerts,
}) => {
  const { isEhrSyncCleared } = useAdminData();
  const [warningPopupOpen, setWarningPopupOpen] = useState<boolean>(false);
  const { pathname } = useLocation();
  const currentRoute =
    (pathname.split("#")?.pop()?.split("/").pop() as ClinicalReportPageRoute) ||
    ("upload-files" as ClinicalReportPageRoute);
  const { getNextRoute, getPastRoute } = useOrderedReportSubRoutes();
  const nextRoute = getNextRoute(currentRoute);
  const pastRoute = getPastRoute(currentRoute);

  const hasUnsavedFields = useHasUnsavedFields();
  const go = useNavigate();

  const goBack = () => {
    if (pastRoute) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      go(`../${pastRoute}`);
    }
  };

  const handlePrimaryButtonAction = async (e?: React.MouseEvent<HTMLElement>) => {
    if (primaryButtonType === "submit") {
      return;
    }

    e?.preventDefault();
    e?.stopPropagation();
    if (nextRoute || skipNavigate) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (primaryButtonAction) {
        try {
          await primaryButtonAction();
          if (!skipNavigate) {
            go(`../${nextRoute}`);
          }
        } catch (_err) {
          console.error("Error calling primaryButtonAction from ClinicalReportFooterButtonRow ");
          notifyError("Failed to perform action");
          return;
        }
      } else {
        if (!skipNavigate) {
          window.scrollTo({ top: 0, behavior: "smooth" });
          go(`../${nextRoute}`);
        }
      }
    }
  };

  const isWarningDisplayed = isEhrSyncCleared && !!(ehrSyncAlerts?.importAlert || ehrSyncAlerts?.exportAlert);

  return (
    <LayoutFooter
      id="btnRow"
      className="flex items-center justify-end border-t border-border-strong px-8 bg-white py-[11.5px]"
    >
      <div className="flex mr-[10px]">
        <WarningTriangle
          onClick={() => {
            setWarningPopupOpen(true);
          }}
          tooltipText="Treatment plan for the learner hasn't been synchronized with EHR completely. Click to see the details."
          visible={isWarningDisplayed}
        />
        <EhrSyncAlertPopup open={warningPopupOpen} setOpen={setWarningPopupOpen} ehrSyncAlerts={ehrSyncAlerts} />
      </div>
      <div className="flex items-center gap-2">
        <FormSavingIndicator />
        {!hideBackButton && <Button appearance="secondary" text="Back" onClick={goBack} />}
        <Tooltip disabled={!(primaryButtonDisabled && primaryButtonDisabledTooltipReason)}>
          <TooltipTrigger>
            <Button
              dataTestID={dataTestID}
              appearance="primary"
              disabled={primaryButtonDisabled || hasUnsavedFields}
              text={primaryButtonLoading ? "Loading..." : primaryButtonTitle}
              type={primaryButtonType}
              onClick={handlePrimaryButtonAction}
            />
          </TooltipTrigger>
          {primaryButtonDisabledTooltipReason && (
            <TooltipContent
              className={cn(typeof primaryButtonDisabledTooltipReason === "string" && "whitespace-pre-line")}
            >
              {primaryButtonDisabledTooltipReason}
            </TooltipContent>
          )}
        </Tooltip>
      </div>
    </LayoutFooter>
  );
};
